import { useCallback, useEffect, useRef, useState } from "react";

import { api, subscribe, type StatsOverview, type ThroughputPoint } from "../lib/api";
import { AnimatedNumber, LiveDot, Sparkline, StackedColumns, type Series } from "../components/charts";
import { Card, Skeleton, duration, relativeTime } from "../components/ui";

/**
 * The chart's three series, in fixed order.
 *
 * The order is the identity mechanism and never changes — a filter or an empty
 * bucket must never repaint the survivors. Colours are the validated chart
 * tokens; see the note beside them in styles.css for why approved is teal and
 * not green.
 */
const SERIES: readonly Series[] = [
  { key: "approved", label: "Approved", color: "var(--chart-approved)" },
  { key: "rejected", label: "Rejected", color: "var(--chart-rejected)" },
  { key: "waiting", label: "Still waiting", color: "var(--chart-pending)" },
];

const RANGES = [
  { days: 1, label: "24h" },
  { days: 7, label: "7d" },
  { days: 14, label: "14d" },
  { days: 30, label: "30d" },
] as const;

interface Activity {
  readonly id: string;
  readonly type: string;
  readonly at: string;
  readonly title: string;
  readonly detail: string;
  readonly tone: "approved" | "rejected" | "submitted" | "neutral";
}

export function Dashboard() {
  const [stats, setStats] = useState<StatsOverview | null>(null);
  const [series, setSeries] = useState<{ bucket: "hour" | "day"; points: ThroughputPoint[] } | null>(null);
  const [models, setModels] = useState<{ model: string; count: number }[]>([]);
  const [latency, setLatency] = useState<{ p50Seconds: number | null; p95Seconds: number | null } | null>(null);
  const [days, setDays] = useState<number>(14);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [pinged, setPinged] = useState(false);
  const [live, setLive] = useState(false);
  const pingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(async (range: number) => {
    const [overview, throughput, top, lat] = await Promise.all([
      api.get<StatsOverview>("/stats/overview"),
      api.get<{ bucket: "hour" | "day"; points: ThroughputPoint[] }>(`/stats/throughput?days=${range}`),
      api.get<{ model: string; count: number }[]>("/stats/models?days=30&limit=6"),
      api.get<{ p50Seconds: number | null; p95Seconds: number | null }>("/stats/latency?days=30"),
    ]);
    setStats(overview);
    setSeries(throughput);
    setModels(top);
    setLatency(lat);
  }, []);

  useEffect(() => {
    void refresh(days).catch(() => undefined);
  }, [refresh, days]);

  /**
   * The live feed.
   *
   * Everything that moves on this page is driven from here, which is the point:
   * an animation that fires on a timer would look identical on a dead system.
   * If the ring pulses, something genuinely happened.
   */
  useEffect(
    () =>
      subscribe(
        [
          "proposal.submitted",
          "proposal.approved",
          "proposal.rejected",
          "proposal.duplicate_blocked",
          "conversation.message_received",
          "conversation.message_sent",
        ],
        (event) => {
          setActivity((prev) => [toActivity(event), ...prev].slice(0, 8));
          setPinged(true);
          if (pingTimer.current) clearTimeout(pingTimer.current);
          pingTimer.current = setTimeout(() => setPinged(false), 1500);
          void refresh(days).catch(() => undefined);
        },
        setLive,
      ),
    [refresh, days],
  );

  if (!stats || !series) {
    return (
      <div className="grid gap-6">
        <Skeleton className="h-8 w-40" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-[104px]" />
          ))}
        </div>
        <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
          <Skeleton className="h-[292px]" />
          <Skeleton className="h-[292px]" />
        </div>
      </div>
    );
  }

  const points = series.points.map((p) => ({
    bucket: p.bucket,
    values: {
      approved: p.approved,
      rejected: p.rejected,
      // Whatever was submitted in this bucket and has not been decided. Kept
      // non-negative: a proposal decided in a later bucket than it arrived
      // would otherwise push this below zero.
      waiting: Math.max(0, p.submitted - p.approved - p.rejected),
    },
  }));

  const submittedTrend = series.points.map((p) => p.submitted);
  const decidedTrend = series.points.map((p) => p.approved + p.rejected);
  const waiting = stats.oldestPendingAgeSeconds;
  const stale = waiting !== null && waiting > 4 * 3600;

  return (
    <div className="grid gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Dashboard</h1>
          <div className="mt-1.5 flex items-center gap-2 text-[13px] text-[var(--muted)]">
            <LiveDot active={live} pinged={pinged} />
            <span>{live ? "Live — updates as decisions are made" : "Reconnecting…"}</span>
          </div>
        </div>

        {/* Filters in one row above the charts, never scattered beside them. */}
        <div className="flex gap-1 rounded-[10px] border border-[var(--border)] bg-[var(--surface-1)] p-1">
          {RANGES.map((r) => (
            <button
              key={r.days}
              onClick={() => setDays(r.days)}
              className={
                "rounded-[7px] px-2.5 py-1 text-[12px] transition-colors duration-150 " +
                (days === r.days
                  ? "bg-[var(--brand-soft)] font-medium text-[var(--brand)]"
                  : "text-[var(--muted)] hover:text-[var(--foreground)]")
              }
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          label="Waiting"
          value={stats.pending}
          tone={stats.pending > 0 ? "warn" : "default"}
          hint={waiting === null ? "Queue is clear" : `Oldest ${duration(waiting)}${stale ? " — chase it" : ""}`}
          trend={series.points.map((p) => Math.max(0, p.submitted - p.approved - p.rejected))}
          trendColor="var(--chart-pending)"
        />
        <StatTile
          label="Approved"
          value={stats.approved}
          tone="approved"
          hint={
            stats.approvalRate === null
              ? "Nothing decided yet"
              : `${Math.round(stats.approvalRate * 100)}% of decisions`
          }
          trend={series.points.map((p) => p.approved)}
          trendColor="var(--chart-approved)"
        />
        <StatTile
          label="Rejected"
          value={stats.rejected}
          tone="rejected"
          hint="All time"
          trend={series.points.map((p) => p.rejected)}
          trendColor="var(--chart-rejected)"
        />
        <StatTile
          label="Last 24 hours"
          value={stats.last24h}
          tone="brand"
          hint="Submitted"
          trend={submittedTrend}
          trendColor="var(--chart-submitted)"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card className="flex h-full flex-col">
          <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-[15px] font-semibold">Throughput</h2>
            <span className="text-[12px] text-[var(--faint)]">
              by {series.bucket} · hover a column
            </span>
          </div>

          {/* flex-1 so the plot absorbs whatever height the row gives this
              card, rather than leaving a gap under the legend. */}
          <div className="min-h-[200px] flex-1">
            <StackedColumns points={points} series={SERIES} bucket={series.bucket} />
          </div>

          {/* A legend is always present for two or more series — identity is
              never left to colour-matching alone. */}
          <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-[var(--border)] pt-3 text-[11px] text-[var(--muted)]">
            {SERIES.map((s) => (
              <span key={s.key} className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-[2px]" style={{ background: s.color }} />
                {s.label}
              </span>
            ))}
            <span className="ml-auto text-[var(--faint)]">
              {submittedTrend.reduce((a, b) => a + b, 0)} submitted ·{" "}
              {decidedTrend.reduce((a, b) => a + b, 0)} decided
            </span>
          </div>
        </Card>

        <div className="grid content-start gap-4">
          <Card>
            <div className="flex items-center justify-between">
              <h2 className="text-[15px] font-semibold">Activity</h2>
              <LiveDot active={live} pinged={pinged} />
            </div>
            {activity.length === 0 ? (
              <p className="mt-3 text-[13px] leading-relaxed text-[var(--faint)]">
                Nothing yet. Events appear here the instant they happen — no refresh.
              </p>
            ) : (
              <ul className="mt-3 grid gap-2.5">
                {activity.map((item) => (
                  <li key={item.id} className="animate-slide-in flex items-start gap-2.5">
                    <span
                      className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                      style={{ background: toneColor(item.tone) }}
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] text-[var(--foreground)]">
                        {item.title}
                      </span>
                      <span className="block truncate text-[11px] text-[var(--faint)]">
                        {item.detail} · {relativeTime(item.at)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <h2 className="text-[15px] font-semibold">Decision speed</h2>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Figure label="Typical" value={duration(latency?.p50Seconds ?? null)} />
              <Figure
                label="Slowest 5%"
                value={duration(latency?.p95Seconds ?? null)}
                tone="var(--chart-rejected)"
              />
            </div>
            <p className="mt-3 text-[12px] leading-relaxed text-[var(--faint)]">
              Percentiles, not an average — one proposal left over a weekend would ruin a mean.
            </p>
          </Card>

          <Card>
            <h2 className="text-[15px] font-semibold">Busiest models</h2>
            {models.length === 0 ? (
              <p className="mt-3 text-[13px] text-[var(--faint)]">Nothing in the last 30 days.</p>
            ) : (
              <ul className="mt-3 grid gap-2.5">
                {models.map((m, i) => {
                  const max = models[0]?.count ?? 1;
                  return (
                    <li key={m.model} className="grid gap-1.5">
                      <div className="flex items-baseline justify-between gap-3 text-[13px]">
                        <span className="truncate">{m.model}</span>
                        <span className="tabular shrink-0 text-[var(--faint)]">{m.count}</span>
                      </div>
                      <div className="h-1.5 overflow-hidden rounded-full bg-[var(--surface-2)]">
                        <div
                          className="h-full rounded-full transition-[width] duration-500 ease-out"
                          style={{
                            width: `${Math.max(3, (m.count / max) * 100)}%`,
                            // One series, one colour — never a value-ramp on
                            // nominal categories, which would double-encode the
                            // length the bar already shows.
                            background: "var(--chart-submitted)",
                            opacity: 1 - i * 0.1,
                          }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MiniStat label="Active API keys" value={stats.activeApiKeys} />
        <MiniStat label="Active webhooks" value={stats.activeWebhooks} />
        <MiniStat label="Unread chats" value={stats.unreadConversations} highlight={stats.unreadConversations > 0} />
        <MiniStat label="Total proposals" value={stats.total} />
      </div>
    </div>
  );
}

function StatTile({
  label,
  value,
  hint,
  tone,
  trend,
  trendColor,
}: {
  label: string;
  value: number;
  hint: string;
  tone: "default" | "warn" | "brand" | "approved" | "rejected";
  trend: readonly number[];
  trendColor: string;
}) {
  const toneClass = {
    default: "text-[var(--foreground)]",
    warn: "text-[var(--warn)]",
    brand: "text-[var(--brand)]",
    approved: "text-[var(--ok)]",
    rejected: "text-[var(--danger)]",
  }[tone];

  return (
    <Card className="relative overflow-hidden">
      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--faint)]">{label}</div>
      <div className="mt-2 flex items-end justify-between gap-2">
        <AnimatedNumber value={value} className={`text-3xl font-semibold leading-none ${toneClass}`} />
        <Sparkline values={trend} color={trendColor} />
      </div>
      <div className="mt-2 text-[13px] text-[var(--muted)]">{hint}</div>
    </Card>
  );
}

function MiniStat({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <Card>
      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--faint)]">{label}</div>
      <AnimatedNumber
        value={value}
        className={`mt-2 block text-3xl font-semibold leading-none ${
          highlight ? "text-[var(--brand)]" : "text-[var(--foreground)]"
        }`}
      />
    </Card>
  );
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.08em] text-[var(--faint)]">{label}</div>
      <div className="tabular mt-1 text-xl font-semibold" style={tone ? { color: tone } : undefined}>
        {value}
      </div>
    </div>
  );
}

function toneColor(tone: Activity["tone"]): string {
  switch (tone) {
    case "approved":
      return "var(--chart-approved)";
    case "rejected":
      return "var(--chart-rejected)";
    case "submitted":
      return "var(--chart-submitted)";
    default:
      return "var(--faint)";
  }
}

/** Turn a raw event envelope into a line a human can read at a glance. */
function toActivity(event: { id: string; type: string; createdAt: string; data: unknown }): Activity {
  const data = (event.data ?? {}) as Record<string, unknown>;
  const model = typeof data["model"] === "string" ? data["model"] : "a model";
  const who = typeof data["decidedByName"] === "string" ? data["decidedByName"] : null;
  const author = typeof data["authorName"] === "string" ? data["authorName"] : null;

  switch (event.type) {
    case "proposal.submitted":
      return {
        id: event.id, type: event.type, at: event.createdAt, tone: "submitted",
        title: `${model} — new proposal`,
        detail: author ? `from ${author}` : "submitted",
      };
    case "proposal.approved":
      return {
        id: event.id, type: event.type, at: event.createdAt, tone: "approved",
        title: `${model} — approved`,
        detail: who ? `by ${who}` : "decided",
      };
    case "proposal.rejected":
      return {
        id: event.id, type: event.type, at: event.createdAt, tone: "rejected",
        title: `${model} — rejected`,
        detail: who ? `by ${who}` : "decided",
      };
    case "proposal.duplicate_blocked":
      return {
        id: event.id, type: event.type, at: event.createdAt, tone: "neutral",
        title: `${model} — duplicate blocked`,
        detail: "same idea, already submitted",
      };
    case "conversation.message_received":
      return {
        id: event.id, type: event.type, at: event.createdAt, tone: "submitted",
        title: "Message received",
        detail: "in a conversation the bot watches",
      };
    case "conversation.message_sent":
      return {
        id: event.id, type: event.type, at: event.createdAt, tone: "neutral",
        title: "Message sent",
        detail: "as the bot",
      };
    default:
      return {
        id: event.id, type: event.type, at: event.createdAt, tone: "neutral",
        title: event.type, detail: "event",
      };
  }
}
